import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import { GridApi, RowSelectedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { Carrier } from "@/pages/mill/carriers/models/Carrier";
import { routeCarrierAssembly } from "@/setup/routes/mill.routes";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { Group, Panel } from "react-resizable-panels";
import { useNavigate } from "react-router-dom";
import { useCarriersAssembly } from "../CarrierAssemblyPage";
import { useTableData } from "../hooks/useTableData";
import { CarrierAssemblyDetails } from "./CarrierAssemblyDetails";

export const TableCarrierAssembly = ({
	partId,
}: {
	partId: string | undefined;
}) => {
	const navigate = useNavigate();

	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<Carrier | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<"assembly" | "disassembly" | "">(
		"",
	);
	const [error, setError] = useState("");

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("carrier-status-table-panel-sizes");

	const {
		data: rows,
		error: e,
		isSuccess,
		isError,
		isLoading,
	} = useCarriersAssembly({});

	const { columnDefs } = useTableData({});

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		if (event.node.isSelected()) {
			setSelectedRowData(event.data);
			setShowModal("assembly");
			navigate(`/${routeCarrierAssembly}/${(event.data as Carrier).PartId}`);
		}
	};

	const onModalHide = ({
		close,
		entityId,
	}: {
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
		}
		if (entityId) {
			setTimeout(() => {
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	useEffect(() => {
		if (!isLoading) {
			if (partId) {
				const partNode = gridApi?.getRowNode(partId);

				if (partNode) {
					gridApi?.ensureNodeVisible(partNode);

					partNode.setSelected(true);
					setSelectedRowData(partNode.data);
					setShowModal("assembly");
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		if (isSuccess) {
			if (selectedRowData && rows) {
				const updatedCarrier = rows.find(
					(c) => c.PartId === selectedRowData.PartId,
				);
				if (updatedCarrier) {
					setSelectedRowData(updatedCarrier);
				}
			}
		}
	}, [isSuccess, rows]);

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	return (
		<>
			{selectedRowData && (
				<HelmetDexteel title={`${selectedRowData.PartName} - Carrier Status`} />
			)}
			<Grid2
				container
				justifyContent="center"
				direction="column"
				p={2}
				spacing={1}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Carriers Status
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => {
							setFilterValue(val);
							gridApi?.setGridOption("quickFilterText", val);
						}}
					/>
				</Grid2>
				<Grid2
					sx={{
						width: "100%",
					}}
				>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							<Paper elevation={2} style={{ width: "100%", height: "100%" }}>
								<AgGridReact<Carrier>
									loading={isLoading}
									rowData={rows || []}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) => String(params.data.PartId)}
									getRowClass={(params) => `row-${params.data?.PartName}`}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									gridOptions={{
										rowSelection: {
											mode: "singleRow",
											checkboxes: false,
											enableClickSelection: true,
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper
								elevation={2}
								style={{
									height: "100%",
									width: "100%",
									overflowY: "auto",
									overflowX: "hidden",
								}}
							>
								<CarrierAssemblyDetails
									key={`${selectedRowData?.PartId}`}
									carrier={selectedRowData}
									onHide={onModalHide}
									modal={showModal}
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
